
/*
* @license
* ANALOG Theme (c) Groupthought Themes
*
* This file is included for advanced development by
* Shopify Agencies. Modified versions of the theme
* code are not supported by Shopify or Groupthought.
*
* Add any minor JS changes in assets/custom.js, this
* will make it easier to update the theme in the future.
*
*/
                  
import Alpine from 'alpinejs';
import { springValue, styleEffect } from 'motion';

(function() {
    const env = {"NODE_ENV":"production"};
    try {
        if (process) {
            process.env = Object.assign({}, process.env);
            Object.assign(process.env, env);
            return;
        }
    } catch (e) {} // avoid ReferenceError: process is not defined
    globalThis.process = { env:env };
})();

/**
 * Returns the token that follows a directive modifier name.
 *
 * @example
 * getModifierValue(['speed', '60'], 'speed');
 * // => '60'
 */
function getModifierValue(modifiers, modifierName) {
    var _a;
    const modifierIndex = modifiers.indexOf(modifierName);
    if (modifierIndex === -1)
        return null;
    return (_a = modifiers[modifierIndex + 1]) !== null && _a !== void 0 ? _a : null;
}
/**
 * Returns a numeric modifier value when it can be parsed as a finite number.
 *
 * @example
 * getNumberModifierValue(['speed', '-60'], 'speed');
 * // => -60
 */
function getNumberModifierValue(modifiers, modifierName) {
    const rawModifierValue = getModifierValue(modifiers, modifierName);
    if (rawModifierValue == null)
        return null;
    const parsedModifierValue = Number(rawModifierValue);
    if (!Number.isFinite(parsedModifierValue))
        return null;
    return parsedModifierValue;
}
/**
 * Returns a positive numeric modifier value.
 *
 * @example
 * getPositiveNumberModifierValue(['stiffness', '240'], 'stiffness');
 * // => 240
 */
function getPositiveNumberModifierValue(modifiers, modifierName) {
    const parsedModifierValue = getNumberModifierValue(modifiers, modifierName);
    if (parsedModifierValue == null || parsedModifierValue <= 0)
        return null;
    return parsedModifierValue;
}

const DEFAULT_MIN_SPRING_MASS = 0.01;
/**
 * Returns a spring mass modifier value using a minimum mass as the scale unit.
 *
 * @example
 * getMassModifierValue(['mass', '0'], 'mass');
 * // => 0.01
 *
 * @example
 * getMassModifierValue(['mass', '20'], 'mass');
 * // => 0.2
 */
function getMassModifierValue(modifiers, modifierName, minimumMass = DEFAULT_MIN_SPRING_MASS) {
    const rawModifierValue = getModifierValue(modifiers, modifierName);
    if (rawModifierValue == null)
        return null;
    const parsedModifierValue = Number(rawModifierValue);
    if (Number.isNaN(parsedModifierValue) || parsedModifierValue < 0)
        return null;
    if (parsedModifierValue === 0)
        return minimumMass;
    return parsedModifierValue * minimumMass;
}
/**
 * Applies `.mass`, `.stiffness`, and `.damping` directive modifiers to a spring config.
 *
 * @example
 * getSpringModifierConfig(['mass', '20', 'stiffness', '180'], {mass: 0.5, stiffness: 200, damping: 30});
 * // => {mass: 0.2, stiffness: 180, damping: 30}
 */
function getSpringModifierConfig(modifiers, defaultSpringConfig, minimumMass = DEFAULT_MIN_SPRING_MASS) {
    const springConfig = Object.assign({}, defaultSpringConfig);
    const parsedMass = getMassModifierValue(modifiers, 'mass', minimumMass);
    if (parsedMass !== null)
        springConfig.mass = parsedMass;
    const parsedStiffness = getPositiveNumberModifierValue(modifiers, 'stiffness');
    if (parsedStiffness !== null)
        springConfig.stiffness = parsedStiffness;
    const parsedDamping = getPositiveNumberModifierValue(modifiers, 'damping');
    if (parsedDamping !== null)
        springConfig.damping = parsedDamping;
    return springConfig;
}

/**
 * Helpers to defer geometry reads until after the browser has applied pending style/layout work.
 *
 * Alpine’s `$nextTick` runs after DOM updates in the microtask queue, but often still in the same
 * frame as other directives. Reading `clientWidth`, `getBoundingClientRect()`, etc. there can force
 * synchronous layout. Prefer scheduling those reads for the next animation frame.
 *
 * **Convention (Alpine plugins):**
 * - After `$nextTick` (when the tree you care about is in the DOM), call `afterNextPaint` or
 *   `readLayoutAfterPaint` before measuring.
 * - After `Alpine.mutateDom` (or any synchronous DOM write), do not read layout in the same turn;
 *   use `readLayoutAfterPaint` (or a follow-up `requestAnimationFrame`) before measuring.
 * - For ongoing size changes, prefer `ResizeObserver` / `x-resize` and keep one-off “first measure”
 *   paths on this schedule.
 * - If deferred reads would leave stale geometry used by other synchronous logic (e.g. scroll
 *   thresholds derived from header heights), measure synchronously when registering — or refactor
 *   that logic so it does not run until after layout is known.
 */
function afterNextPaint(fn) {
    return requestAnimationFrame(fn);
}

/**
 * Logs a clear, actionable console error when a Method UI directive is used
 * without a parent directive it depends on.
 *
 * Many directives only work when nested inside a parent directive that sets up
 * shared state on the Alpine scope (e.g. `x-overflow:start` needs an ancestor
 * `x-overflow`). When that parent is missing, the raw failure is an inscrutable
 * `this.__foo is not a function`. Call this instead so users get a message that
 * explains what to do.
 *
 * @param childDirective - The directive being used, e.g. `'x-overflow:start'`.
 * @param parentDirective - The parent directive required, e.g. `'x-overflow'`.
 * @param el - The element the child directive is on, logged for context.
 *
 * @example
 * ```ts
 * if (typeof this.__overflow_registerBoundary !== 'function') {
 *   warnMissingParentDirective(`x-overflow:${value}`, 'x-overflow', $el);
 *   return;
 * }
 * ```
 */
function warnMissingParentDirective(childDirective, parentDirective, el) {
    console.error(`Cannot use ${childDirective} without a parent element having ${parentDirective}.`, el);
}

const MIN_MASS = 0.01;
const SPRING_DEFAULT_CONFIG = {
    stiffness: 120,
    mass: MIN_MASS,
};
/**
 * Plugin to create a custom cursor that follows the mouse with spring physics,
 * snaps to snap points, and supports named cursor variants.
 *
 * Inspired by https://medium.com/14islands/developing-a-performant-custom-cursor-89f1688a02eb
 *
 * ## Directives
 *
 * - `x-custom-cursor` — Root element. Tracks mouse enter/leave/move.
 * - `x-custom-cursor:element` — Cursor element, positioned via spring-animated translateX/Y.
 *   Accepts an optional name (e.g. `x-custom-cursor:element="plus"`) for named cursors.
 *   Unnamed elements act as the default cursor.
 * - `x-custom-cursor:snap-point` — Snaps the cursor to this element's center on hover.
 *
 * ## Spring modifiers (on `x-custom-cursor:element`)
 *
 * - `.mass.<n>` — Spring mass (default: {@link SPRING_DEFAULT_CONFIG}). Higher = heavier/slower.
 * - `.stiffness.<n>` — Spring stiffness. Higher = snappier.
 * - `.damping.<n>` — Spring damping. Higher = less oscillation.
 *
 * ## Data attributes (on descendant elements inside the root)
 *
 * - `data-custom-cursor="<name>"` — Shows the named cursor when hovering this element.
 * - `data-hide-custom-cursor` — Hides all cursors when hovering this element.
 *
 * ## Reactive data attributes (set automatically on cursor/snap-point elements)
 *
 * - `data-active` — Present on a cursor element when it is the currently active cursor.
 * - `data-hidden` — Present when the cursor is over a `data-hide-custom-cursor` zone
 *   or when the cursor element would overflow the root bounds.
 * - `data-snapped` — Present when the cursor is snapped to a snap point.
 *
 * ## Magic: `$customCursor`
 *
 * - `$customCursor.isActive` — Whether the cursor is inside the root.
 * - `$customCursor.isSnapped` — Whether the cursor is snapped to a snap point.
 * - `$customCursor.isHidden` — Whether the cursor is in a hide zone.
 * - `$customCursor.targetPosition` — Current target `{x, y}` in viewport coordinates.
 * - `$customCursor.snap(el)` — Programmatically snap to an element.
 * - `$customCursor.unsnap()` — Programmatically unsnap.
 *
 * @example
 *
 * ```html
 * <div x-custom-cursor>
 *   <div x-custom-cursor:element class="fixed top-0 left-0 w-5 h-5 bg-red-500"></div>
 *   <div x-custom-cursor:element.mass.20="plus" class="fixed top-0 left-0 w-8 h-8 bg-blue-500"></div>
 *   <div x-custom-cursor:snap-point class="absolute top-1/2 left-1/2 w-12 h-12"></div>
 *   <div data-custom-cursor="plus">Hover for plus cursor</div>
 *   <div data-hide-custom-cursor>Hover to hide cursor</div>
 * </div>
 * ```
 */
function customCursor (Alpine) {
    Alpine.directive('custom-cursor', ($el, data) => {
        if (data.value === 'element') {
            handleElement($el, Alpine, data);
        }
        else if (data.value === 'snap-point') {
            handleSnapPoint($el, Alpine);
        }
        else if (data.value === null) {
            handleRoot($el, Alpine);
        }
        else {
            throw new Error(`Unknown directive: x-custom-cursor:${data.value}`);
        }
    }).before('bind');
    Alpine.magic('customCursor', ($el) => {
        const $data = Alpine.$data($el);
        return {
            get isSnapped() {
                return $data.__customCursor_isSnapped;
            },
            get isHidden() {
                return $data.__customCursor_isHidden;
            },
            get isActive() {
                return $data.__customCursor_isActive;
            },
            get targetPosition() {
                return $data.__customCursor_targetPosition;
            },
            snap(el) {
                $data.__customCursor_setCurrentSnapPoint(el);
            },
            unsnap() {
                $data.__customCursor_unsetCurrentSnapPoint();
            },
        };
    });
}
function handleElement($el, Alpine, data) {
    const cursorName = data.expression || null;
    const springConfig = getSpringModifierConfig(data.modifiers, SPRING_DEFAULT_CONFIG, MIN_MASS);
    Alpine.bind($el, {
        'aria-hidden': 'true',
        'x-data'() {
            return {
                __customCursor_cursorEl_xValue: null,
                __customCursor_cursorEl_yValue: null,
                __customCursor_cursorEl_stopStyleEffect: null,
                __customCursor_cursorEl_dimensions: null,
                __customCursor_cursorEl_afterNextPaintId: null,
                init() {
                    if (!this.$data.__customCursor_rootEl) {
                        warnMissingParentDirective('x-custom-cursor:element', 'x-custom-cursor', this.$el);
                        return;
                    }
                    this.$nextTick(() => {
                        this.__customCursor_cursorEl_afterNextPaintId = afterNextPaint(() => {
                            this.__customCursor_cursorEl_afterNextPaintId = null;
                            this.__customCursor_cursorEl_dimensions = { width: this.$el.clientWidth, height: this.$el.clientHeight };
                            const xValue = springValue(0, springConfig);
                            const yValue = springValue(0, springConfig);
                            this.__customCursor_cursorEl_xValue = xValue;
                            this.__customCursor_cursorEl_yValue = yValue;
                            this.__customCursor_cursorEl_stopStyleEffect = styleEffect(this.$el, { x: xValue, y: yValue });
                            this.$watch('__customCursor_targetPosition', (targetPosition, previousTargetPosition) => {
                                if (targetPosition == null) {
                                    xValue.stop();
                                    yValue.stop();
                                    return;
                                }
                                const { width, height } = this.__customCursor_cursorEl_dimensions;
                                const x = targetPosition.x - width / 2;
                                const y = targetPosition.y - height / 2;
                                if (previousTargetPosition == null) {
                                    xValue.jump(x);
                                    yValue.jump(y);
                                }
                                else {
                                    xValue.set(x);
                                    yValue.set(y);
                                }
                            });
                        });
                    });
                },
                destroy() {
                    var _a, _b, _c;
                    if (this.__customCursor_cursorEl_afterNextPaintId !== null) {
                        cancelAnimationFrame(this.__customCursor_cursorEl_afterNextPaintId);
                        this.__customCursor_cursorEl_afterNextPaintId = null;
                    }
                    (_a = this.__customCursor_cursorEl_xValue) === null || _a === void 0 ? void 0 : _a.stop();
                    (_b = this.__customCursor_cursorEl_yValue) === null || _b === void 0 ? void 0 : _b.stop();
                    (_c = this.__customCursor_cursorEl_stopStyleEffect) === null || _c === void 0 ? void 0 : _c.call(this);
                },
                get __customCursor_isAtEdge() {
                    const rootData = this.$data;
                    const targetPosition = rootData.__customCursor_targetPosition;
                    const dimensions = this.__customCursor_cursorEl_dimensions;
                    const rootEl = rootData.__customCursor_rootEl;
                    if (!targetPosition || !dimensions || !rootEl)
                        return false;
                    const rootRect = rootEl.getBoundingClientRect();
                    const halfW = dimensions.width / 2;
                    const halfH = dimensions.height / 2;
                    return (targetPosition.x - halfW < rootRect.left ||
                        targetPosition.x + halfW > rootRect.right ||
                        targetPosition.y - halfH < rootRect.top ||
                        targetPosition.y + halfH > rootRect.bottom);
                },
            };
        },
        ':data-active'() {
            const rootData = this.$data;
            const isNameMatch = cursorName == null
                ? rootData.__customCursor_activeCursorName == null
                : rootData.__customCursor_activeCursorName === cursorName;
            if (!rootData.__customCursor_isActive || !isNameMatch)
                return null;
            return '';
        },
        ':data-hidden'() {
            const rootData = this.$data;
            return rootData.__customCursor_isHidden;
        },
        ':data-edge'() {
            return this.__customCursor_isAtEdge;
        },
        ':data-snapped'() {
            const rootData = this.$data;
            return rootData.__customCursor_isSnapped;
        },
        ':style'() {
            const rootData = this.$data;
            const isNameMatch = cursorName == null
                ? rootData.__customCursor_activeCursorName == null
                : rootData.__customCursor_activeCursorName === cursorName;
            if (this.__customCursor_cursorEl_xValue == null ||
                this.__customCursor_cursorEl_dimensions == null ||
                !rootData.__customCursor_isActive ||
                !isNameMatch) {
                return {
                    contain: 'layout',
                    visibility: 'hidden',
                    'pointer-events': 'none',
                };
            }
            return {
                transformOrigin: '50% 50% 0',
                'will-change': 'transform',
                contain: 'layout',
                'pointer-events': 'none',
            };
        },
    });
}
function handleSnapPoint($el, Alpine) {
    // Bail early if we're not inside of x-custom-cursor
    if (!Alpine.$data($el).__customCursor_rootEl) {
        warnMissingParentDirective('x-custom-cursor:snap-point', 'x-custom-cursor', $el);
        return;
    }
    Alpine.bind($el, {
        'x-data'() {
            return {
                __customCursor_snapPoint_handleMouseEnter() {
                    this.$data.__customCursor_setCurrentSnapPoint(this.$el);
                },
                __customCursor_snapPoint_handleMouseLeave() {
                    this.$data.__customCursor_unsetCurrentSnapPoint();
                },
            };
        },
        ':data-snapped'() {
            const rootData = this.$data;
            return rootData.__customCursor_currentSnapPointEl === this.$el;
        },
        'x-hover:start': '__customCursor_snapPoint_handleMouseEnter',
        'x-hover:end': '__customCursor_snapPoint_handleMouseLeave',
    });
}
function buildPosition(x, y) {
    return {
        x,
        y,
    };
}
function handleRoot($el, Alpine, data) {
    Alpine.bind($el, {
        'x-data'() {
            return {
                __customCursor_rootEl: $el,
                __customCursor_isActive: false,
                __customCursor_isHidden: false,
                __customCursor_activeCursorName: null,
                __customCursor_cursorEl: null,
                __customCursor_cursorEl_dimensions: null,
                __customCursor_currentSnapPointEl: null,
                __customCursor_currentSnapPointRect: null,
                __customCursor_targetPosition: null,
                __customCursor_abortController: null,
                init() {
                    // Fire events if the pointer is already inside the `$el` on mount
                    if ($el.matches(':hover')) {
                        this.__customCursor_attachActiveListeners();
                    }
                },
                destroy() {
                    var _a;
                    (_a = this.__customCursor_abortController) === null || _a === void 0 ? void 0 : _a.abort();
                },
                __customCursor_setCurrentSnapPoint(el) {
                    const { x, y, width, height } = el.getBoundingClientRect();
                    this.__customCursor_currentSnapPointEl = el;
                    this.__customCursor_currentSnapPointRect = { x, y, width, height };
                },
                __customCursor_unsetCurrentSnapPoint() {
                    this.__customCursor_currentSnapPointEl = null;
                    this.__customCursor_currentSnapPointRect = null;
                },
                __customCursor_handleMouseOver(e) {
                    const target = e.target;
                    const hideEl = target.closest('[data-hide-custom-cursor]');
                    const customCursorEl = target.closest('[data-custom-cursor]');
                    if (hideEl) {
                        this.__customCursor_isHidden = true;
                        this.__customCursor_activeCursorName = null;
                    }
                    else if (customCursorEl) {
                        this.__customCursor_isHidden = false;
                        this.__customCursor_activeCursorName = customCursorEl.getAttribute('data-custom-cursor');
                    }
                    else {
                        this.__customCursor_isHidden = false;
                        this.__customCursor_activeCursorName = null;
                    }
                },
                __customCursor_handleMouseEnter(e) {
                    this.__customCursor_targetPosition = buildPosition(e.clientX, e.clientY);
                    this.__customCursor_attachActiveListeners();
                },
                __customCursor_attachActiveListeners() {
                    if (this.__customCursor_abortController)
                        return;
                    const ac = new AbortController();
                    this.__customCursor_abortController = ac;
                    let lastMoveTime = 0;
                    $el.addEventListener('mousemove', (e) => {
                        const now = Date.now();
                        if (now - lastMoveTime < 16)
                            return;
                        lastMoveTime = now;
                        this.__customCursor_handleMouseMove(e);
                    }, { signal: ac.signal });
                    $el.addEventListener('mouseover', (e) => this.__customCursor_handleMouseOver(e), {
                        signal: ac.signal,
                    });
                    $el.addEventListener('mouseleave', (e) => this.__customCursor_handleMouseLeave(e), {
                        signal: ac.signal,
                    });
                },
                __customCursor_handleMouseMove(e) {
                    this.__customCursor_isActive = true;
                    if (this.__customCursor_currentSnapPointEl) {
                        const { x: snapX, y: snapY, width: snapWidth, height: snapHeight, } = this.__customCursor_currentSnapPointRect;
                        const snapPosition = {
                            x: snapX + snapWidth / 2,
                            y: snapY + snapHeight / 2,
                        };
                        this.__customCursor_targetPosition = snapPosition;
                    }
                    else {
                        this.__customCursor_targetPosition = buildPosition(e.clientX, e.clientY);
                    }
                },
                __customCursor_handleMouseLeave(e) {
                    var _a;
                    (_a = this.__customCursor_abortController) === null || _a === void 0 ? void 0 : _a.abort();
                    this.__customCursor_abortController = null;
                    this.__customCursor_isActive = false;
                    this.__customCursor_isHidden = false;
                    this.__customCursor_activeCursorName = null;
                    this.__customCursor_currentSnapPointEl = null;
                    this.__customCursor_targetPosition = null;
                },
                get __customCursor_isSnapped() {
                    return !!this.__customCursor_currentSnapPointEl;
                },
            };
        },
        '@mouseenter': '__customCursor_handleMouseEnter',
    });
}

Alpine.plugin(customCursor);
